import {z} from 'zod';
import {zDateTime, zNumberToString} from '@brunata-angular/util';

export const sftpAuthenticationTypes = ['SSH_KEY', 'PASSWORD'] as const;

export type SftpAuthenticationType = (typeof sftpAuthenticationTypes)[number];

export const sftpAuthenticationLabelKeys: Record<SftpAuthenticationType, string> = {
    SSH_KEY: 'sftpconnection_modal_authentication_ssh_key',
    PASSWORD: 'sftpconnection_modal_authentication_password',
};

export const defaultSftpPort = 22;

export const sftpConnectionSchema = z.object({
    sftpConnectionId: zNumberToString,
    name: z.string(),
    host: z.string(),
    port: z.number(),
    username: z.string(),
    authenticationType: z.enum(sftpAuthenticationTypes),
    baseFolder: z.string(),
    testPath: z.string(),
    usedByExportCount: z.number(),
    latestSavedExportId: z.number().nullable().catch(null),
    isTestServer: z.boolean().optionalUntilDeployed('AGI-20667'),
});

export type SftpConnection = z.infer<typeof sftpConnectionSchema>;

export const sftpConnectionPageSchema = z.object({
    sftpConnections: z.array(sftpConnectionSchema),
});

export type SftpConnectionPage = z.infer<typeof sftpConnectionPageSchema>;

export const sftpConnectionTestResultSchema = z.object({succeeded: z.boolean()});

export type SftpConnectionCredentials = {
    host: string;
    port: number;
    username: string;
    authenticationType: SftpAuthenticationType;
    baseFolder: string;
    testPath: string | null;
    /** Only sent when the user picked a new key; null keeps the stored one. */
    privateKey: string | null;
    /** Only sent when the user typed a new password; null keeps the stored one. */
    password: string | null;
};

export type SftpConnectionRequest = SftpConnectionCredentials & {
    name: string;
};

export type SftpConnectionTestRequest = SftpConnectionCredentials & {
    /** Null while testing a connection that has not been created yet. */
    sftpConnectionId: string | null;
};

export type SftpConnectionRow = {
    connection: SftpConnection;
    hostLabel: string;
    baseFolderLabel: string;
    usedByCountText: string;
};

export const toSftpConnectionRow = (connection: SftpConnection): SftpConnectionRow => ({
    connection,
    hostLabel:
        connection.port === defaultSftpPort
            ? connection.host
            : `${connection.host}:${connection.port}`,
    baseFolderLabel: connection.baseFolder.startsWith('/')
        ? connection.baseFolder
        : ` ${connection.baseFolder}`,
    usedByCountText: connection.usedByExportCount.toString(10),
});

export const sftpConnectionTestStageSchema = z.enum(['connect', 'login', 'sftp', 'write']);

export type SftpConnectionTestStage = z.infer<typeof sftpConnectionTestStageSchema>;

export const sftpConnectionTestStages = sftpConnectionTestStageSchema.options;

export type SftpConnectionTestState = 'running' | 'succeeded' | 'failed';

export type SftpConnectionTestProgress = {
    state: SftpConnectionTestState;
    details: Partial<Record<SftpConnectionTestStage, string>>;
};

export const sftpTestServerFileSchema = z.object({
    folder: z.string(),
    fileName: z.string(),
    sizeBytes: z.number(),
    modifiedAt: zDateTime,
});

export type SftpTestServerFile = z.infer<typeof sftpTestServerFileSchema>;

export const sftpTestServerFilesSchema = z.object({
    files: z.array(sftpTestServerFileSchema).default([]),
    failureMessage: z.string().nullable().catch(null),
});

export type SftpTestServerFiles = z.infer<typeof sftpTestServerFilesSchema>;
